"use memo"

import {
  AutopilotTaskDto,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client'
import {
  AgxModal,
  AgxSpacer,
  AgxStepsIndicator,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2'
import { useEffect, useRef, useState } from 'react'
import TaskModalActionButtons from './TaskModalActionButtons'
import { CampaignDetailModel, DocumentGroup } from '@urbanx/agx.campaigns.client'
import TasksFilesList from 'pages/campaigns/components/campaignTasks/TasksFilesList'
import { TaskModalPageProps } from './TaskModalPageProps'
import { useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates'
import { applyTaskUpdateToTasks } from 'utils/tasks'
import { TaskUpdatedNotification } from 'types/pushNotifications'

interface TaskModalDialogProps {
  title: string
  tasks: AutopilotTaskDto[]
  open: boolean
  onClose: () => void
  documents: DocumentGroup[]
  campaignId: string
  campaign: CampaignDetailModel
  onSaveCurrentPage?: (task: AutopilotTaskDto) => void | Promise<void>
  renderPage: (props: TaskModalPageProps) => React.ReactNode
  completeDisabled?: boolean
}

const TaskModalDialog = ({
  title,
  tasks,
  open,
  onClose,
  documents,
  campaignId,
  campaign,
  onSaveCurrentPage,
  renderPage,
  completeDisabled = false,
}: TaskModalDialogProps) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [localTasks, setLocalTasks] = useState<AutopilotTaskDto[]>(tasks)
  const persistedTasksRef = useRef<AutopilotTaskDto[]>(tasks)
  const wasOpenRef = useRef(open)

  useEffect(() => {
    if (open) return

    setLocalTasks(tasks)
    persistedTasksRef.current = tasks
    setCurrentPage(0)
  }, [tasks, open])

  useEffect(() => {
    if (open) {
      setLocalTasks(tasks)
      persistedTasksRef.current = tasks
      if (!wasOpenRef.current) {
        setCurrentPage(0)
      }
    }

    wasOpenRef.current = open
  }, [open, tasks])

  const handleTaskUpdate = (taskUpdate: TaskUpdatedNotification) => {
    persistedTasksRef.current = applyTaskUpdateToTasks(
      persistedTasksRef.current,
      taskUpdate
    )
    setLocalTasks((prevTasks) => applyTaskUpdateToTasks(prevTasks, taskUpdate))
  }

  useAutopilotTaskUpdates(handleTaskUpdate)

  const currentTask = localTasks[currentPage]
  const areAllTaskStepsChecked = localTasks.every(
    (task) =>
      (task.taskSteps?.length ?? 0) > 0 &&
      (task.taskSteps ?? []).every((step) => step.isCompleted)
  )

  const { pushToast } = useToast()

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: (updatedTask) => {
      if (!updatedTask) return

      const taskUpdate: TaskUpdatedNotification = { task: updatedTask }
      setLocalTasks((prev) => applyTaskUpdateToTasks(prev, taskUpdate))
      persistedTasksRef.current = applyTaskUpdateToTasks(
        persistedTasksRef.current,
        taskUpdate
      )
    },
    onError: () => {
      pushToast({
        type: 'error',
        message: 'Failed to toggle task steps',
      })
    },
  })

  const toggleTaskStep = (stepOrder: number, isChecked: boolean) => {
    setLocalTasks((prev) =>
      prev.map((task, index) => {
        if (index !== currentPage) {
          return task
        }

        return {
          ...task,
          taskSteps:
            task.taskSteps?.map((step) =>
              step.order === stepOrder
                ? { ...step, isCompleted: isChecked }
                : step
            ) ?? [],
        }
      })
    )
  }

  const persistCurrentPage = async () => {
    const persistedTask = persistedTasksRef.current[currentPage]
    const changedStepIds =
      currentTask?.taskSteps
        ?.filter((step) => {
          const persistedStep = persistedTask?.taskSteps?.find(
            (candidate) => candidate.order === step.order
          )

          return (
            (persistedStep?.isCompleted ?? false) !==
            (step.isCompleted ?? false)
          )
        })
        .map((step) => step.order ?? 0) ?? []

    if (onSaveCurrentPage && currentTask) {
      await onSaveCurrentPage(currentTask)
    }

    if (changedStepIds.length > 0 && currentTask?.category) {
      toggleTaskSteps({
        campaignId,
        category: currentTask.category,
        contractId: currentTask.contractId,
        stepIds: changedStepIds,
      })

      persistedTasksRef.current = persistedTasksRef.current.map((task, index) =>
        index === currentPage ? currentTask : task
      )
    }
  }

  const onPrevious = async () => {
    await persistCurrentPage()
    setCurrentPage((prev) => Math.max(prev - 1, 0))
  }

  const onNext = async () => {
    await persistCurrentPage()
    setCurrentPage((prev) => Math.min(prev + 1, localTasks.length - 1))
  }

  const onSaveAndExit = () => {
    persistCurrentPage()
    onClose()
  }

  const onComplete = () => {
    persistCurrentPage()
    onClose()
  }

  return (
    <AgxModal
      type="fullScreen"
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      setIsDialogOpen={onClose}
      isOpen={open}
      secondaryTitle={
        <AgxSpacer direction="horizontal" contentAlignment="center" fill>
          <div
            style={{
              display: 'grid',
              width: '100%',
              gap: 'var(--gap-xl)',
              gridTemplateColumns: '2fr 1fr',
            }}
          >
            <AgxTypography
              type="body_ui_strong"
              size="medium"
              id="titlemodal"
              color="var(--text-primary)"
            >
              {title}
            </AgxTypography>
            <AgxStepsIndicator
              totalLevels={localTasks.length}
              currentLevel={currentPage + 1}
            />
          </div>
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="horizontal"
          padding={{
            top: 'md',
            bottom: 'md',
            left: 'xl',
            right: 'xl',
          }}
          fill
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '3fr 2fr',
              width: '100%',
              gap: 'var(--gap-xl)',
            }}
          >
            <AgxSpacer fill>
              <TasksFilesList files={documents} id={campaignId} />
            </AgxSpacer>
            <AgxSpacer direction="vertical" fill>
              {currentTask ? (
                renderPage({
                  task: currentTask,
                  taskNumber: currentPage + 1,
                  campaign,
                  toggleTaskStep,
                })
              ) : null}
            </AgxSpacer>
          </div>
        </AgxSpacer>
      }
      actionButtons={
        <TaskModalActionButtons
          currentPage={currentPage}
          totalPages={localTasks.length}
          onPrevious={onPrevious}
          onNext={onNext}
          onComplete={onComplete}
          onSaveAndExit={onSaveAndExit}
          completeDisabled={completeDisabled || !areAllTaskStepsChecked}
        />
      }
    />
  )
}

export default TaskModalDialog
